import {
  Component,
  ViewEncapsulation,
  OnChanges,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { ParseDateTimePipe } from 'src/shared/pipes/parse-date.pipe';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { ReservationsOfDayPage } from '../reservations-of-day/reservations-of-day';
import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import {
  AvailabilityRequestResponse,
  Job,
  OwnerBlock,
} from 'src/models/schedule-weekly-view.model';
import { PastPrivateJobPage } from 'src/pages/more/past-private-job/past-private-job';
import { PastJobPage } from 'src/pages/more/past-job/past-job';
import { JobPage } from '../job/job';
import { ReservationPage } from 'src/pages/reservation/reservation';
import { Util } from 'src/shared/util/util';
import { AddressModel } from 'src/models/address.model';
import { DateTime } from 'luxon';
import { PendingProRequestPage } from 'src/pages/pending-pro-request/pending-pro-request';
import { WindowService } from 'src/shared/providers/window.service';

@Component({
    templateUrl: 'summary-of-day.html',
    styleUrls: ['./summary-of-day.scss'],
    selector: 'tidy-summary-of-day',
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class SummaryOfDayPage implements OnChanges, OnInit {
  isRightSideContent = true;
  dialogParams: any;
  propertiesWithEvents: any;
  pageTitle: string;

  constructor(
    private storage: TidyStorage,
    private rightSidePanelService: RightSidePanelService,
    private store: TidyStorage,
    private navCtrl: CustomNavController,
    private util: Util,
    private windowService: WindowService
  ) {}

  ngOnChanges(_changes: SimpleChanges) {
    // No changes to process
  }

  async ngOnInit() {
    localStorage.setItem('jobPageBackPath', 'summary-of-day');
    this.isRightSideContent =
      (await this.storage.retrieve('dialog-right-side-open')) || false;
    this.dialogParams = await this.storage.retrieve('dialog-params');
    if (!this.dialogParams?.day?.fullDate) {
      this.navCtrl.back();
      return;
    }
    this.propertiesWithEvents = this.parsePropertiesWithEvents(
      this.dialogParams.day,
      this.dialogParams.properties
    );
    this.addCheckInCheckOutBoolean();
    this.dialogParams = {
      ...this.dialogParams,
      propertiesWithEvents: this.propertiesWithEvents,
    };
    this.store.save('dialog-params', this.dialogParams);
    if (this.dialogParams.type === 'reservation') {
      this.goToReservationsWithoutHistoryContext();
      return;
    }
    const parsedDate = new ParseDateTimePipe().transform(
      this.dialogParams.day.fullDate,
      'dddd MMM d'
    );
    this.rightSidePanelService.setDialogPageTitle(parsedDate);
    this.pageTitle = parsedDate;
  }

  addCheckInCheckOutBoolean() {
    this.propertiesWithEvents.map((property) => {
      property.items.map((item) => {
        const isReservation = item?.checkInTime && !item?.parsedRequestTimes;
        if (isReservation) {
          if (item.startDate === this.dialogParams.day.fullDate) {
            item['isCheckIn'] = true;
          } else {
            item['isCheckIn'] = false;
          }
        }
      });
      property.items.sort((a, b) => {
        const dateA = a.period?.startDate || a.startDate;
        const dateB = b.period?.startDate || b.startDate;
        if (!dateA && !dateB) {return 0;}
        if (!dateA) {return 1;}
        if (!dateB) {return -1;}
        return DateTime.fromISO(dateA) < DateTime.fromISO(dateB) ? -1 : 1;
      });
    });
  }

  parsePropertiesWithEvents(day: any, properties: any) {
    if (!properties) {return [];}
    return properties
      .map((property: any) => {
        const jobsAtThisDay = property.filteredJobs.filter(
          (job) => job.startDate === day.fullDate
        );
        const availabilityRequestsAtThisDay =
          property.filteredAvailabilityRequests.filter(
            (job) => job.period.startDate === day.fullDate
          );
        const reservationsStartingThisDay = property.reservations.filter(
          (reservation) => reservation.period.startDate === day.fullDate
        );
        const reservationsEndingThisDay = property.reservations.filter(
          (reservation) => reservation.period.endDate === day.fullDate
        );
        // Stamped on the day by the monthly view, one per property blocked that night.
        const ownerBlocks = (day.ownerBlocks ?? []).filter(
          (block: OwnerBlock) => block.addressId === property?.id
        );
        const items = [
          ...jobsAtThisDay,
          ...reservationsStartingThisDay,
          ...reservationsEndingThisDay,
          ...availabilityRequestsAtThisDay,
        ];
        return {
          id: property?.id,
          name: property?.name,
          address: property?.address,
          items: this.sortItems(items, day.fullDate),
          ownerBlocks,
          reservationInfo: {
            starting: reservationsStartingThisDay.length,
            ending: reservationsEndingThisDay.length,
          },
        };
      })
      .filter((property) => property.items.length > 0 || property.ownerBlocks.length > 0);
  }

  onBlockUnblocked(property: any, block: OwnerBlock): void {
    property.ownerBlocks = property.ownerBlocks.filter((ownerBlock: OwnerBlock) => ownerBlock.id !== block.id);
    this.propertiesWithEvents = this.propertiesWithEvents.filter(
      (item: any) => item.items.length > 0 || item.ownerBlocks.length > 0
    );
  }

  sortItems(items, date) {
    const sortedItems = [];

    items.forEach((item) => {
      const isReservationCheckingOutToday =
        item?.period?.endDate === date && item?.checkInTime;
      const isJob = item?.booking || item?.parsedRequestTimes;
      const isReservationCheckingInToday =
        item?.period?.endDate !== date && item?.checkInTime;

      if (isReservationCheckingOutToday) {
        sortedItems.push(item);
      } else if (isJob) {
        sortedItems.push(item);
      } else if (isReservationCheckingInToday) {
        sortedItems.push(item);
      }
    });

    return sortedItems;
  }

  goToJobPage(cleaning: any) {
    if (cleaning?.parsedRequestTimes) {
      this.handleNavigatingAvailabilityRequest(cleaning);
      return;
    }
    const addressId = cleaning?.address_id;
    const isMobileResolution = !this.windowService.isDesktopRes;
    const isPastJob =
      cleaning.status.name === 'cancelled' ||
      cleaning.status.name === 'completed';
    const isPrivateJob = cleaning.isPrivate;
    if (!isMobileResolution) {
      this.navigateToJobPageInsideDialog(
        cleaning,
        addressId,
        isPastJob,
        isPrivateJob
      );
      return;
    }
    localStorage.setItem('pastJobBackPage', 'schedule');
    if (isPastJob) {
      if (isPrivateJob) {
        this.navCtrl.navigateForward([`past-private-job/${cleaning.id}`]);
      } else {
        this.navCtrl.navigateForward([`past-job/${cleaning.id}`]);
      }
    } else {
      this.navCtrl.navigateForward([`job/${addressId}/${cleaning.id}`], {
        cleaning,
      });
    }
  }

  async handleNavigatingAvailabilityRequest(cleaning: Job) {
    // TODO: Monthly filters
    const loading = await this.util.showLoading();
    const availabilityRequest: AvailabilityRequestResponse = cleaning?.object;
    if (availabilityRequest.status === 'countered') {
      this.goToRequestProCounter(availabilityRequest, cleaning.address);
    } else {
      this.editProRequest(availabilityRequest, cleaning.address);
    }
    loading.dismiss();
    return;
  }

  goToRequestProCounter(
    availabilityRequest: AvailabilityRequestResponse,
    address: AddressModel
  ) {
    const params = {
      planFrequency: availabilityRequest.frequency,
      service: availabilityRequest.service_type_details,
      proposedTimes: availabilityRequest.request_times,
      proName: availabilityRequest.homekeeper?.name.split(' ')[0],
      hkId: availabilityRequest.homekeeper?.id,
      availabilityRequestId: availabilityRequest.id,
      addressId: address.id,
      addressName: address.address_name,
    };
    this.navCtrl.navigateForward('request-pro-counter', params);
  }

  async editProRequest(
    availabilityRequest: AvailabilityRequestResponse,
    address: AddressModel
  ) {
    try {
      const isMobileResolution = !this.windowService.isDesktopRes;
      const params = {
        cameFromEditRequest: true,
        availabilityRequestId: availabilityRequest.id,
        requestTimes: availabilityRequest.request_times,
        serviceType: availabilityRequest.service_type_details.key,
        times: availabilityRequest.request_times,
        frequency: {
          viewValue: availabilityRequest.frequency,
          value: availabilityRequest.frequency,
        },
        pro: {
          first_name: availabilityRequest.homekeeper?.name.split(' ')[0],
          id: availabilityRequest.homekeeper?.id,
        },
        address,
      };
      if (!isMobileResolution) {
        this.storage.save('dialog-right-side-open', true);
        this.storage.save('dialog-params', params);
        this.rightSidePanelService.navigateTo(
          'pending-pro-request',
          params,
          PendingProRequestPage
        );
      } else {
        this.navCtrl.navigateForward('pending-pro-request', params);
      }
    } catch (err) {
      const errorMessage = err.error ? err.error.message : err.message;
      this.util.showError(errorMessage);
    }
  }

  navigateToJobPageInsideDialog(
    cleaning: Job,
    addressId: number,
    isPastJob: boolean,
    isPrivateJob: boolean
  ): void {
    const params = {
      jobId: cleaning.id,
      addressId,
      ...cleaning,
    };
    this.storage.save('dialog-right-side-open', true);
    if (isPastJob && isPrivateJob) {
      this.rightSidePanelService.navigateTo(
        `past-private-job/${cleaning.id}`,
        params,
        PastPrivateJobPage
      );
    } else if (isPastJob) {
      this.rightSidePanelService.navigateTo(
        `past-job/${cleaning.id}`,
        params,
        PastJobPage
      );
    } else {
      this.rightSidePanelService.navigateTo(
        `job/${addressId}/${cleaning.id}`,
        params,
        JobPage
      );
    }
  }

  goToReservationsWithoutHistoryContext() {
    const isMobileResolution = !this.windowService.isDesktopRes;
    if (!isMobileResolution) {
      this.storage.save('dialog-right-side-open', true);
      this.rightSidePanelService.openRightSidePanel(ReservationsOfDayPage);
    } else {
      this.storage.save('dialog-right-side-open', false);
      this.navCtrl.navigateForward('summary-of-day', this.dialogParams);
    }
  }

  goToReservations() {
    this.rightSidePanelService.navigateTo(
      'reservations-of-day',
      {},
      ReservationsOfDayPage
    );
  }

  goToReservation(reservation: any) {
    const addressName =
      reservation?.address?.address_name ||
      reservation?.address?.address1 +
        (reservation?.address?.address2
          ? ` ${  reservation?.address?.address2}`
          : '');
    const guestReservation = {
      ...reservation,
      check_in_date: reservation?.object?.check_in_date,
      check_out_date: reservation?.object?.check_out_date,
      check_in_time: reservation?.object?.check_in_time,
      check_out_time: reservation?.object?.check_out_time,
    };
    const params = {
      guestReservation,
      address: addressName,
      addressId: reservation?.address?.id
    };
    this.rightSidePanelService.navigateTo(
      'reservation',
      params,
      ReservationPage
    );
  }
}
